import { useEffect, useState } from 'react';
import { getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import api from '../../../../../api';

const EMPTY = {
  clave: '',
  nombre: '',
  descripcion: '',
  idEmpresa: '',
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
  defaultEmpresaId = null,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    clave: '',
    nombre: '',
    descripcion: '',
  });
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const userEmpresaId = Number(getSessionEmpresaId());
  const isSuperAdmin = !userEmpresaId || userEmpresaId === 0;

  // Carga jerárquica de empresas — idéntico a TipoUnidadOrgModal
  // El select aparece:
  //   - Siempre si es SuperAdmin
  //   - Si hay hijas cuya empresa NO tiene tieneUOs (disponibles sin uso)
  useEffect(() => {
    async function loadHierarchy() {
      if (!open) return;

      setLoadingCompanies(true);
      try {
        if (isSuperAdmin) {
          // SuperAdmin ve todas las empresas
          const res = await api.get(ENDPOINTS.empresas);
          const raw = res.data || [];
          setAvailableCompanies(raw.map(e => ({
            idEmpresa: e.idEmpresa ?? e.IdEmpresa,
            nombre: e.nombre ?? e.Nombre
          })));
        } else {
          // Cargar empresa actual y sus hijas
          const [resCurrent, resChildren] = await Promise.all([
            api.get(ENDPOINTS.empresasById(userEmpresaId)),
            api.get(`${ENDPOINTS.empresas}/hijas/${userEmpresaId}`)
          ]);

          const current = resCurrent.data;
          const children = resChildren.data || [];

          const normalizedCurrent = {
            idEmpresa: current.idEmpresa ?? current.IdEmpresa,
            nombre: current.nombre ?? current.Nombre
          };

          // Solo incluir hijas disponibles — idéntico a TipoUnidadOrgModal
          const normalizedChildren = children.map(e => ({
            idEmpresa: e.idEmpresa ?? e.IdEmpresa,
            nombre: e.nombre ?? e.Nombre
          }));

          if (normalizedChildren.length > 0) {
            setAvailableCompanies([normalizedCurrent, ...normalizedChildren]);
          } else {
            // Sin hijas disponibles — el select no aparece
            setAvailableCompanies([]);
          }
        }
      } catch (err) {
        console.error('Error loading company hierarchy', err);
      } finally {
        setLoadingCompanies(false);
      }
    }

    loadHierarchy();
  }, [open, isSuperAdmin, userEmpresaId]);

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'clave':
        if (!value) error = 'La clave es obligatoria.';
        break;
      case 'nombre':
        if (!value) error = 'El nombre es obligatorio.';
        break;
      case 'descripcion':
        if (!value) error = 'La descripción es obligatoria.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['clave', 'nombre', 'descripcion'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        clave: data.clave ?? '',
        nombre: data.nombre ?? '',
        descripcion: data.descripcion ?? '',
        idEmpresa: data.idEmpresa ?? '',
      };
      setForm(filled);
      validateField('clave', filled.clave);
      validateField('nombre', filled.nombre);
      validateField('descripcion', filled.descripcion);
    } else {
      setForm({
        ...EMPTY,
        idEmpresa: defaultEmpresaId ? String(defaultEmpresaId) : (isSuperAdmin ? '' : String(userEmpresaId)),
      });
      setErrors({ clave: '', nombre: '', descripcion: '' });
    }
  }, [mode, data, isSuperAdmin, userEmpresaId, open, defaultEmpresaId]);

  if (!open) return null;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled = !form.clave?.trim() || !form.nombre?.trim() || !form.descripcion?.trim() || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva zona geográfica' : 'Editar zona geográfica'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Clave:</label>
        <br /><br />
        <input
          className={`infoField${errors.clave ? ' infoField--error' : ''}`}
          value={form.clave}
          onChange={(e) => handleChange('clave', e.target.value)}
          maxLength={50}
        />
        {errors.clave && <small style={errorStyle}>{errors.clave}</small>}
      </div>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre:</label>
        <br /><br />
        <input
          className={`infoField${errors.nombre ? ' infoField--error' : ''}`}
          value={form.nombre}
          onChange={(e) => handleChange('nombre', e.target.value)}
          maxLength={100}
        />
        {errors.nombre && <small style={errorStyle}>{errors.nombre}</small>}
      </div>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Descripción:</label>
        <br /><br />
        <textarea
          className={`infoField${errors.descripcion ? ' infoField--error' : ''}`}
          value={form.descripcion}
          onChange={(e) => handleChange('descripcion', e.target.value)}
          maxLength={255}
          rows={4}
        />
        {errors.descripcion && <small style={errorStyle}>{errors.descripcion}</small>}
      </div>

      {/* Select de empresa — solo aparece cuando hay hijas disponibles o es SuperAdmin */}
      {(availableCompanies.length > 0 || isSuperAdmin) && (
        <div style={{ gridColumn: '1 / 3' }}>
          <label className="roles-title" style={{ fontSize: '.95rem' }}>Empresa / Sucursal:</label>
          <br /><br />
          {loadingCompanies ? (
            <span style={{ fontSize: '.85rem', color: '#64748b' }}>Cargando empresas…</span>
          ) : (
            <select
              className="infoField"
              value={form.idEmpresa}
              onChange={(e) => handleChange('idEmpresa', e.target.value)}
            >
              {isSuperAdmin && <option value="">Global (Aplica a todas)</option>}
              {availableCompanies.map(emp => (
                <option key={emp.idEmpresa} value={emp.idEmpresa}>
                  {emp.nombre} {Number(emp.idEmpresa) === userEmpresaId ? '(Actual)' : ''}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        paddingBottom: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) {
            const payload = {
              ...form,
              idEmpresa: form.idEmpresa === '' ? null : Number(form.idEmpresa),
            };
            onSubmit(payload, mode);
          }
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(720px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
